import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "../../components/BackLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Users,
  Star,
  Loader2,
  Send,
  CheckCircle,
  Clock,
  User,
  ChevronRight,
  Plus,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayerFeedback({ user }) {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Feedback form state
  const [feedbackType, setFeedbackType] = useState("performance");
  const [rating, setRating] = useState(0);
  const [strengths, setStrengths] = useState([]);
  const [areasToImprove, setAreasToImprove] = useState([]);
  const [notes, setNotes] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [newStrength, setNewStrength] = useState("");
  const [newImprovement, setNewImprovement] = useState("");

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/coach/dashboard`, {
        withCredentials: true,
      });
      setTeams(response.data.teams || []);
      if (response.data.teams?.length > 0) {
        setSelectedTeam(response.data.teams[0]);
        await fetchPlayers(response.data.teams[0].id);
        await fetchFeedbackHistory(response.data.teams[0].id);
      }
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async (teamId) => {
    try {
      setLoadingPlayers(true);
      const response = await axios.get(
        `${API}/academy/coach/team/${teamId}/roster`,
        { withCredentials: true }
      );
      setPlayers(response.data.players || []);
    } catch (err) {
      console.error("Error fetching players:", err);
      toast.error("Failed to load players");
    } finally {
      setLoadingPlayers(false);
    }
  };

  const fetchFeedbackHistory = async (teamId) => {
    try {
      const response = await axios.get(
        `${API}/academy/coach/team/${teamId}/feedback`,
        { withCredentials: true }
      );
      setFeedbackHistory(response.data.feedback || []);
    } catch (err) {
      console.error("Error fetching feedback:", err);
    }
  };

  const handleTeamChange = async (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    setSelectedTeam(team);
    await fetchPlayers(teamId);
    await fetchFeedbackHistory(teamId);
  };

  const openFeedbackForm = (player) => {
    setSelectedPlayer(player);
    setFeedbackType("performance");
    setRating(0);
    setStrengths([]);
    setAreasToImprove([]);
    setNotes("");
    setIsPrivate(false);
    setShowFeedbackForm(true);
  };

  const addStrength = () => {
    if (newStrength.trim() && !strengths.includes(newStrength.trim())) {
      setStrengths([...strengths, newStrength.trim()]);
      setNewStrength("");
    }
  };

  const addImprovement = () => {
    if (newImprovement.trim() && !areasToImprove.includes(newImprovement.trim())) {
      setAreasToImprove([...areasToImprove, newImprovement.trim()]);
      setNewImprovement("");
    }
  };

  const submitFeedback = async () => {
    if (!notes.trim()) {
      toast.error("Please add some notes");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${API}/academy/coach/feedback`,
        {
          player_id: selectedPlayer.id,
          team_id: selectedTeam.id,
          feedback_type: feedbackType,
          rating: rating > 0 ? rating : null,
          strengths,
          areas_to_improve: areasToImprove,
          notes,
          private: isPrivate,
        },
        { withCredentials: true }
      );

      toast.success("Feedback submitted successfully");
      setShowFeedbackForm(false);
      await fetchFeedbackHistory(selectedTeam.id);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <BackLink to="/academy/coach-dashboard" label="Back"
            className="text-white hover:bg-white/20 mb-2 -ml-2"
            testId="player-feedback-back" />
          <h1 className="text-2xl font-bold">Player Feedback</h1>
          <p className="text-amber-200">Provide assessments and feedback for your players</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Team Selector */}
        {teams.length > 1 && (
          <div className="mb-6">
            <Select value={selectedTeam?.id} onValueChange={handleTeamChange}>
              <SelectTrigger className="w-64 bg-slate-800 border-slate-700">
                <SelectValue placeholder="Select Team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Players List */}
          <Card className="bg-slate-900/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" /> Team Players
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingPlayers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                </div>
              ) : players.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No players in this team</p>
              ) : (
                <div className="space-y-3">
                  {players.map((player) => (
                    <div
                      key={player.id}
                      className="p-4 bg-[#0a0f1a] rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer"
                      onClick={() => openFeedbackForm(player)}
                      data-testid={`player-${player.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{player.name}</p>
                            <p className="text-sm text-slate-400">
                              {player.position || "Player"}
                            </p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20">
                          <Plus className="w-4 h-4 mr-1" /> Feedback
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Feedback */}
          <Card className="bg-slate-900/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" /> Recent Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              {feedbackHistory.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No feedback submitted yet</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {feedbackHistory.map((feedback) => (
                    <div
                      key={feedback.id}
                      className="p-4 bg-[#0a0f1a] rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{feedback.player_name}</span>
                        <Badge className={
                          feedback.feedback_type === "performance" ? "bg-blue-500/20 text-blue-400" :
                          feedback.feedback_type === "behavior" ? "bg-purple-500/20 text-purple-400" :
                          feedback.feedback_type === "progress" ? "bg-green-500/20 text-green-400" :
                          "bg-slate-500/20 text-slate-400"
                        }>
                          {feedback.feedback_type}
                        </Badge>
                      </div>
                      {feedback.rating && (
                        <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= feedback.rating ? "text-yellow-400 fill-yellow-400" : "text-slate-600"}`}
                            />
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-slate-300 line-clamp-2">{feedback.notes}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        {new Date(feedback.created_at).toLocaleDateString()}
                        {feedback.private && (
                          <span className="ml-2 text-amber-400">(Private)</span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feedback Form Dialog */}
      <Dialog open={showFeedbackForm} onOpenChange={setShowFeedbackForm}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              Feedback for {selectedPlayer?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Feedback Type */}
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Feedback Type</label>
              <Select value={feedbackType} onValueChange={setFeedbackType}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="behavior">Behavior</SelectItem>
                  <SelectItem value="progress">Progress</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Rating */}
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Rating (Optional)</label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star === rating ? 0 : star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= rating ? "text-yellow-400 fill-yellow-400" : "text-slate-600 hover:text-slate-400"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Strengths */}
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Strengths</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newStrength}
                  onChange={(e) => setNewStrength(e.target.value)}
                  placeholder="Add a strength..."
                  className="bg-slate-800 border-slate-700"
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addStrength())}
                />
                <Button type="button" onClick={addStrength} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {strengths.map((s, i) => (
                  <Badge key={i} className="bg-green-500/20 text-green-400">
                    {s}
                    <button onClick={() => setStrengths(strengths.filter((_, idx) => idx !== i))} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Areas to Improve */}
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Areas to Improve</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newImprovement}
                  onChange={(e) => setNewImprovement(e.target.value)}
                  placeholder="Add an area..."
                  className="bg-slate-800 border-slate-700"
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addImprovement())}
                />
                <Button type="button" onClick={addImprovement} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {areasToImprove.map((a, i) => (
                  <Badge key={i} className="bg-orange-500/20 text-orange-400">
                    {a}
                    <button onClick={() => setAreasToImprove(areasToImprove.filter((_, idx) => idx !== i))} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Notes *</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Write your feedback..."
                className="bg-slate-800 border-slate-700 min-h-[100px]"
              />
            </div>

            {/* Private Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="private"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="rounded border-slate-600"
              />
              <label htmlFor="private" className="text-sm text-slate-400">
                Keep this feedback private (not visible to parents)
              </label>
            </div>

            {/* Submit Button */}
            <Button
              onClick={submitFeedback}
              disabled={submitting || !notes.trim()}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="submit-feedback-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
